const SW_URL = "/sw.js";
const PUSH_SW_URL = "/push-sw.js";
const PUSH_SW_SCOPE = "/push-notifications/";

function isAppWorkerBlocked(): boolean {
  if (typeof window === "undefined") return true;
  // Den inbyggda appens webbvy stödjer inte service workers – offline-läget
  // där bygger helt på uppkopplingsvakten (navigator.onLine + server-ping).
  if ("AppWrapper" in window) return true;
  if (!import.meta.env.PROD) return true;
  try {
    if (window.self !== window.top) return true;
  } catch {
    return true;
  }
  const host = window.location.hostname;
  if (host.startsWith("id-preview--") || host.startsWith("preview--")) return true;
  if (host === "lovableproject.com" || host.endsWith(".lovableproject.com")) return true;
  if (host === "lovableproject-dev.com" || host.endsWith(".lovableproject-dev.com")) return true;
  if (host === "beta.lovable.dev" || host.endsWith(".beta.lovable.dev")) return true;
  return new URLSearchParams(window.location.search).get("sw") === "off";
}

async function unregisterAppWorkers() {
  if (!("serviceWorker" in navigator)) return;
  const registrations = await navigator.serviceWorker.getRegistrations().catch(() => []);
  await Promise.allSettled(
    registrations
      .filter((registration) => {
        const script =
          registration.active?.scriptURL ??
          registration.waiting?.scriptURL ??
          registration.installing?.scriptURL ??
          "";
        return new URL(script, window.location.origin).pathname === SW_URL;
      })
      .map((registration) => registration.unregister()),
  );
}

/**
 * Rensar cachar som inte hör till den här siten – t.ex. rester från en
 * tidigare site som legat på samma domän. Körs en gång per session.
 */
async function cleanupLegacyCaches() {
  if (typeof caches === "undefined") return;
  const keep = ["sellavo-pages-v2", "sellavo-assets-v2", "sellavo-images-v2"];
  const names = await caches.keys().catch(() => [] as string[]);
  const stale = names.filter((name) => {
    if (keep.includes(name)) return false;
    // Behåll aktuell Workbox-precache för den här registreringens scope.
    if (name.includes("precache") && name.endsWith(window.location.origin + "/")) return false;
    return true;
  });
  await Promise.allSettled(stale.map((name) => caches.delete(name)));
}

/** True när webbläsaren och anslutningen kan köra push-workern. */
export function pushServiceWorkerAllowed(): boolean {
  return (
    typeof window !== "undefined" &&
    window.isSecureContext &&
    "serviceWorker" in navigator
  );
}

/**
 * Notiser använder en egen worker och är därför inte beroende av appens offline-cache.
 */
export async function ensurePushServiceWorker(): Promise<ServiceWorkerRegistration | null> {
  if (!pushServiceWorkerAllowed()) return null;
  try {
    const registration =
      (await navigator.serviceWorker.getRegistration(PUSH_SW_SCOPE)) ??
      (await navigator.serviceWorker.register(PUSH_SW_URL, {
        scope: PUSH_SW_SCOPE,
        updateViaCache: "none",
      }));

    if (!registration.active) {
      const worker = registration.installing ?? registration.waiting;
      if (worker) {
        await new Promise<void>((resolve) => {
          if (worker.state === "activated") {
            resolve();
            return;
          }
          const done = () => {
            if (worker.state !== "activated" && worker.state !== "redundant") return;
            worker.removeEventListener("statechange", done);
            resolve();
          };
          worker.addEventListener("statechange", done);
        });
      }
    }

    return registration;
  } catch (error) {
    console.error("[push] notis-workern kunde inte startas:", error);
    return null;
  }
}

export function registerServiceWorker() {
  if (typeof window === "undefined" || !("serviceWorker" in navigator)) return;
  if (isAppWorkerBlocked()) {
    void unregisterAppWorkers();
    return;
  }
  void (async () => {
    try {
      const registration =
        (await navigator.serviceWorker.getRegistration("/")) ??
        (await navigator.serviceWorker.register(SW_URL, {
          scope: "/",
          updateViaCache: "none",
        }));
      void registration.update();
    } catch (error) {
      console.error("[pwa] offline-stödet kunde inte startas:", error);
    }
    void cleanupLegacyCaches();
  })();
}

